import axios from "axios";
import { toast } from "react-toastify";
import { setAuthToken } from "./Api";

const BACKEND = process.env.REACT_APP_BACKEND_SERVER || "";

/**
 * Fetch metadata from any URL (IPFS, Cloudinary, or HTTP). Used for token_uri.
 */
export const getMetadata = async (token_uri) => {
  try {
    const metadata = (await axios.get(token_uri)).data;
    return metadata;
  } catch (error) {
    console.log(error);
  }
};

/**
 * Upload metadata JSON to Cloudinary (raw file). Replaces Pinata pinJSONToIPFS.
 * @param {object} metadata - JSON-serializable object
 * @param {string} name - Base name for the file
 * @param {string} _jwt - Unused (kept for backward compatibility)
 * @returns {Promise<string>} Public URL of the uploaded metadata
 */
export const pinMetadata = async (metadata, name, _jwt) => {
  toast.loading("Uploading Metadata..");
  try {
    const url = `${BACKEND}api/upload/metadata${name ? `?name=${encodeURIComponent(name)}` : ""}`;
    const response = await fetch(url, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: setAuthToken(),
      },
      body: JSON.stringify(metadata),
    });
    const data = await response.json();
    toast.dismiss();
    if (!response.ok || !data.url) {
      throw new Error(data.error || "Upload failed");
    }
    toast.success("Metadata uploaded.", { autoClose: 1000 });
    return data.url;
  } catch (error) {
    toast.dismiss();
    toast.error(`Uploading failed: ${error.message || error}`, { autoClose: 1000 });
    console.error(error);
    throw error;
  }
};

/**
 * No-op for Cloudinary (assets are not "unpinned"). Kept for API compatibility.
 */
export const deletePinsFromIPFS = async (hashesToUnpin, _jwt) => {
  if (hashesToUnpin.length) toast.loading("Deleting images..");
  // Cloudinary does not require client-side unpin; skip or call backend delete if needed later
  if (hashesToUnpin.length) {
    toast.dismiss();
    toast.success("Images deleted.", { autoClose: 1000 });
  }
};

/**
 * Convert image URL to File object
 */
export const urlToFile = async (imageUrl, filename = "image.jpg") => {
  try {
    const response = await fetch(imageUrl, {
      mode: "cors",
      credentials: "omit",
    });

    if (!response.ok) {
      throw new Error(`Failed to fetch image: ${response.statusText}`);
    }

    const blob = await response.blob();
    const urlExtension = imageUrl.split(".").pop().split("?")[0];
    const validExtensions = ["jpg", "jpeg", "png", "gif", "webp"];
    const extension = validExtensions.includes(urlExtension.toLowerCase())
      ? urlExtension.toLowerCase()
      : "jpg";

    const finalFilename = filename.includes(".")
      ? filename
      : `${filename.split(".")[0]}.${extension}`;

    return new File([blob], finalFilename, { type: blob.type || "image/jpeg" });
  } catch (error) {
    console.error("Error converting URL to File:", error);
    throw error;
  }
};

/**
 * Convert array of image objects (with data_url) to File objects
 */
export const convertImageUrlsToFiles = async (images) => {
  const convertedImages = [];

  for (let i = 0; i < images.length; i++) {
    const img = images[i];

    if (img && img.file instanceof File) {
      convertedImages.push(img);
      continue;
    }

    const imageUrl = typeof img === "string" ? img : img?.data_url || img?.src;

    if (!imageUrl) {
      console.warn(`Skipping image ${i}: No URL found`);
      continue;
    }

    try {
      const file = await urlToFile(imageUrl, `airbnb-image-${i + 1}.jpg`);
      convertedImages.push({ data_url: imageUrl, file });
    } catch (error) {
      console.error(`Failed to convert image ${i} (${imageUrl}):`, error);
    }
  }

  return convertedImages;
};

/**
 * Upload images to Cloudinary via backend. Replaces Pinata pinImagesToIPFS.
 * @param {Array<{ file: File }>} images - Array of objects with .file (File)
 * @param {string} _jwt - Unused (kept for backward compatibility)
 * @param {string} _gateWay - Unused (kept for backward compatibility)
 * @returns {Promise<string[]>} Array of image URLs
 */
export const pinImagesToIPFS = async (images, _jwt, _gateWay) => {
  if (!images || images.length === 0) {
    console.warn("pinImagesToIPFS: No images provided");
    return [];
  }

  toast.loading(`Uploading ${images.length} images...`);
  const hashes = [];
  let successCount = 0;
  let failCount = 0;

  for (let i = 0; i < images.length; i++) {
    if (!images[i] || !images[i].file) {
      console.error(`Image ${i} is missing file property:`, images[i]);
      failCount++;
      continue;
    }

    if (!(images[i].file instanceof File) && !(images[i].file instanceof Blob)) {
      console.error(`Image ${i} file is not a valid File/Blob:`, typeof images[i].file);
      failCount++;
      continue;
    }
  }

  const validImages = images.filter(
    (img) => img?.file && (img.file instanceof File || img.file instanceof Blob)
  );
  if (validImages.length === 0) {
    toast.dismiss();
    toast.error("No valid images to upload.", { autoClose: 3000 });
    return [];
  }

  try {
    const formData = new FormData();
    validImages.forEach((img) => formData.append("images", img.file));

    const response = await fetch(`${BACKEND}api/upload/images`, {
      method: "POST",
      headers: {
        Authorization: setAuthToken(),
      },
      body: formData,
    });

    const data = await response.json();
    toast.dismiss();

    if (!response.ok) {
      throw new Error(data.error || "Upload failed");
    }

    if (data.urls && Array.isArray(data.urls)) {
      successCount = data.urls.length;
      if (successCount === validImages.length) {
        toast.success(`All ${validImages.length} images uploaded successfully!`, {
          autoClose: 2000,
        });
      } else if (successCount > 0) {
        toast.warning(
          `Uploaded ${successCount} of ${validImages.length} images.`,
          { autoClose: 3000 }
        );
      }
      console.log(
        `pinImagesToIPFS (Cloudinary) completed: ${successCount} success, returning ${data.urls.length} URLs`
      );
      return data.urls;
    }

    toast.error("Upload failed: invalid response.", { autoClose: 3000 });
    return [];
  } catch (error) {
    toast.dismiss();
    console.error("Upload images error:", error);
    toast.error(`Upload failed: ${error.message || "Please try again."}`, {
      autoClose: 3000,
    });
    return [];
  }
};
